"use client"

import { useState, useRef, useEffect } from "react"
import { signOut } from "next-auth/react"
import { revokeTokens } from "@/app/actions/revoke-token"
import ProfileEditModal from "./ProfileEditModal"

interface UserDropdownProps {
  userName: string
  onNameUpdate?: (newName: string) => void
}

export default function UserDropdown({ userName, onNameUpdate }: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [currentName, setCurrentName] = useState(userName)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen])

  const handleLogout = async () => {
    setIsLoggingOut(true)

    try {
      // First revoke tokens and destroy sessions on the backend
      await revokeTokens()

      // Then sign out from NextAuth without redirect
      await signOut({ redirect: false })

      // Force a full page reload to ensure the session is cleared
      window.location.reload()
    } catch (error) {
      console.error("Logout error:", error)
      setIsLoggingOut(false)
    }
  }

  const handleEditProfile = () => {
    setIsOpen(false)
    setIsModalOpen(true)
  }

  const handleNameUpdate = (newName: string) => {
    setCurrentName(newName)
    onNameUpdate?.(newName)
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-800"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span>Welcome, {currentName}</span>
          <svg
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            <button
              onClick={handleEditProfile}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100"
            >
              Edit Profile
            </button>
            <div className="my-1 border-t border-gray-100" />
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="block w-full px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoggingOut ? "Signing Out..." : "Sign Out"}
            </button>
          </div>
        )}
      </div>

      <ProfileEditModal
        currentName={currentName}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleNameUpdate}
      />
    </>
  )
}
